import { useOpenModal } from '@flowus/common/next-modal';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
import type { WelfareCodeBatchVO } from '@next-space/fe-api-idl';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { ListView } from 'src/common/components/list-view';
import { ListItemType } from 'src/common/components/list-view/types';
import { message } from 'src/common/components/message';
import { request } from 'src/common/request';

/** 兑换码详情（后端实际返回的字段） */
interface WelfareCodeDetail {
  code?: string;
  expirationAt?: number;
  usedAt?: boolean;
}

/** 格式化日期时间 */
const formatDateTime = (timestamp?: number): string => {
  if (!timestamp) return '';
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(timestamp);
  }
};

/** 我的兑换码列表 */
export const WelfareCodeList: FC = () => {
  const { t } = useAppTranslation();
  const openModal = useOpenModal();
  const [loading, setLoading] = useState(true);
  const [batches, setBatches] = useState<WelfareCodeBatchVO[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await request.infra.getPurchasedWelfareCodes();
        setBatches(res.batches ?? []);
      } catch (error) {
        message.error(t('views.settings.welfare_code.failed_to_get_list'));
      } finally {
        setLoading(false);
      }
    };
    void fetchData();
  }, []);

  // 下载兑换码（包含过期时间和使用状态）
  const downloadCodes = (batch: WelfareCodeBatchVO, format: 'csv' | 'txt') => {
    const codes = batch.codes as WelfareCodeDetail[];
    if (!codes || codes.length === 0) {
      message.warning(t('views.settings.welfare_code.no_code_data'));
      return;
    }

    // 使用 pageTitle 作为文件名，如果没有则使用批次编号
    const fileBaseName = t('views.settings.welfare_code.filename_prefix', {
      name: batch.pageTitle || batch.batchNo,
    });
    let content = '';
    let filename = '';

    if (format === 'csv') {
      // CSV 格式：包含表头
      const header = t('views.settings.welfare_code.csv_header');
      const rows = codes.map((item) => {
        const code = item.code ?? '';
        const expirationAt = formatDateTime(item.expirationAt);
        const used = item.usedAt ? t('common.utils.yes') : t('common.utils.no');
        return `${code},${expirationAt},${used}`;
      });
      content = [header, ...rows].join('\n');
      filename = `${fileBaseName}.csv`;
    } else {
      // TXT 格式
      const rows = codes.map((item) => {
        const code = item.code ?? '';
        const expirationAt = item.expirationAt
          ? t('views.settings.welfare_code.expiration_time_label', {
              time: formatDateTime(item.expirationAt),
            })
          : '';
        const used = t('views.settings.welfare_code.used_label', {
          used: item.usedAt ? t('common.utils.yes') : t('common.utils.no'),
        });
        return [code, expirationAt, used].filter(Boolean).join(' | ');
      });
      content = rows.join('\n');
      filename = `${fileBaseName}.txt`;
    }

    const blob = new Blob([`\ufeff${content}`], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    message.success(t('views.settings.welfare_code.download_success'));
  };

  // 打开下载格式选择
  const openDownloadMenu = (e: React.MouseEvent, batch: WelfareCodeBatchVO) => {
    openModal.dropdown({
      popcorn: e.currentTarget,
      content: ({ onCloseModal }) => (
        <ListView
          className="next-modal w-[120px] py-1"
          items={[
            {
              type: ListItemType.TEXT_DESCRIPTION,
              data: {
                title: t('views.settings.welfare_code.csv_format'),
                onClick: () => {
                  downloadCodes(batch, 'csv');
                  onCloseModal();
                },
              },
            },
            {
              type: ListItemType.TEXT_DESCRIPTION,
              data: {
                title: t('views.settings.welfare_code.txt_format'),
                onClick: () => {
                  downloadCodes(batch, 'txt');
                  onCloseModal();
                },
              },
            },
          ]}
        />
      ),
    });
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-grey3">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col px-1">
      {/* 表头 */}
      <div className="grid w-full grid-cols-12 gap-x-2 border-b py-2.5 text-t2 text-grey3">
        <div className="col-span-3">{t('views.settings.welfare_code.page_title')}</div>
        <div className="col-span-2">{t('views.settings.welfare_code.batch_number')}</div>
        <div className="col-span-1">{t('common.pay_fee_amount')}</div>
        <div className="col-span-1">{t('views.settings.welfare_code.single_value')}</div>
        <div className="col-span-1">{t('views.settings.welfare_code.total_quantity')}</div>
        <div className="col-span-2">{t('views.settings.welfare_code.generation_time')}</div>
        <div className="col-span-2">{t('common.action')}</div>
      </div>

      {/* 数据行 */}
      <div className="h-full w-full overflow-y-auto">
        {batches.map((batch) => {
          const batchWithPage = batch as WelfareCodeBatchVO;
          return (
            <div
              key={batch.batchNo}
              className="grid w-full grid-cols-12 gap-x-2 border-b border-grey6 py-2.5 text-t2"
            >
              <div className="col-span-3 truncate" title={batchWithPage.pageTitle}>
                {batchWithPage.pageId ? (
                  <a
                    href={`/${batchWithPage.pageId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer text-active_color hover:underline"
                  >
                    {batchWithPage.pageTitle || t('views.settings.member_groups.unnamed')}
                  </a>
                ) : (
                  batchWithPage.pageTitle || '-'
                )}
              </div>
              <div className="col-span-2 truncate" title={batch.batchNo}>
                {batch.batchNo.slice(-8)}
              </div>
              <div className="col-span-1">
                {batch.paidAmount} {t('common.yuan')}
              </div>
              <div className="col-span-1">
                {batch.singleValue} {t('common.yuan')}
              </div>
              <div className="col-span-1">{batch.totalCount}</div>
              <div className="col-span-2">{formatDateTime(batch.createdAt)}</div>
              <div className="col-span-2">
                <span
                  className="cursor-pointer text-active_color"
                  onClick={(e) => openDownloadMenu(e, batch)}
                >
                  {t('common.download')}
                </span>
              </div>
            </div>
          );
        })}

        {/* 空状态 */}
        {batches.length === 0 && (
          <div className="mt-10 text-center text-t4 text-grey3">
            {t('views.settings.welfare_code.no_records')}
          </div>
        )}
      </div>
    </div>
  );
};
